"use client";

import {JSX, type ReactNode, useMemo} from "react";

import {type Placement} from "@floating-ui/react";
import {type SwipeableHandlers} from "react-swipeable";

import {useMatchMedia} from "@/shared/hooks";
import {type DateRangePickerInstance} from "@/shared/models/form/datepickers";
import {ButtonBase} from "@/shared/ui/buttons";
import {Typography} from "@/shared/ui/data-display/typography";
import {type CalendarDay} from "@/shared/ui/form/datepickers/parts";

import {useDateRangePicker, useSwipeAnimation} from "./hooks";
import {DateRangeFilterTrigger, DualCalendar, SingleCalendar} from "./parts";

import "./daterange-picker-field.scss";

export interface DateRangePickerTriggerProps {
    startDate: Date | null;
    endDate: Date | null;
    startPlaceholder: string;
    endPlaceholder: string;
    isOpen: boolean;
    isLoading: boolean;
    disabled: boolean;
    hasValue: boolean;
    onClick: () => void;
    onClear: () => void;
    formatDisplayValue: (date: Date | null) => string;
}

type DateRangePickerRenderTrigger = (props: DateRangePickerTriggerProps) => ReactNode;

interface DateRangePickerFieldProps {
    model: DateRangePickerInstance;
    label?: string;
    startPlaceholder?: string;
    endPlaceholder?: string;
    className?: string;
    showClearButton?: boolean;
    isLoading?: boolean;
    singleCalendar?: boolean;
    renderTrigger?: DateRangePickerRenderTrigger;
    renderHeader?: (props: {startDate: Date | null; endDate: Date | null; nightsCount: number | null; onClear: () => void}) => ReactNode;
    children?: ReactNode;
    disableClickOutside?: boolean;
    controlledIsOpen?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    placement?: Placement;
    fullscreenOnMobile?: boolean;
    enableAutoScroll?: boolean;
    inlineOnMobile?: boolean;
    dropdownZIndex?: number;
    dropdownOffset?: number;
    getDayTooltip?: (date: Date) => string | null | undefined;
    getDayClassName?: (date: Date) => string | null | undefined;
    /** When true, days from previous/next month are still rendered for layout but show no text. */
    hideOtherMonthDaysContent?: boolean;
    /** Rendered to the right of the calendar (inline mode), e.g. mobile "next month" arrow. */
    rightOfCalendar?: ReactNode;
}

// Loading overlay component
function LoadingOverlay(): JSX.Element {
    return (
        <div className="daterange-picker-field__loading-overlay">
            <div className="daterange-picker-field__spinner"></div>
        </div>
    );
}

// Calendar container with swipe support
interface CalendarContainerProps {
    model: DateRangePickerInstance;
    isSingle: boolean;
    swipeHandlers: SwipeableHandlers;
    animationDirection: "left" | "right" | null;
    onDayClick: (day: CalendarDay) => void;
    onDayHover: (date: Date) => void;
    onDayLeave: () => void;
    getDayTooltip?: (date: Date) => string | null | undefined;
    getDayClassName?: (date: Date) => string | null | undefined;
    hideOtherMonthDaysContent?: boolean;
}

function CalendarContainer({
    model,
    isSingle,
    swipeHandlers,
    animationDirection,
    onDayClick,
    onDayHover,
    onDayLeave,
    getDayTooltip,
    getDayClassName,
    hideOtherMonthDaysContent,
}: CalendarContainerProps): JSX.Element {
    const CalendarComponent = isSingle ? SingleCalendar : DualCalendar;

    return (
        <div
            {...swipeHandlers}
            className={`daterange-picker-field__calendars ${animationDirection ? `daterange-picker-field__calendars--slide-${animationDirection}` : ""}`}
        >
            <CalendarComponent
                model={model}
                onDayClick={onDayClick}
                onDayHover={onDayHover}
                onDayLeave={onDayLeave}
                getDayTooltip={getDayTooltip}
                getDayClassName={getDayClassName}
                hideOtherMonthDaysContent={hideOtherMonthDaysContent}
            />
        </div>
    );
}

export function DateRangePickerField(props: DateRangePickerFieldProps): JSX.Element {
    const {
        model,
        label,
        startPlaceholder = "Start date",
        endPlaceholder = "End date",
        className,
        showClearButton = true,
        isLoading = false,
        singleCalendar = false,
        renderTrigger = (triggerProps) => <DateRangeFilterTrigger {...triggerProps} />,
        renderHeader,
        children,
        disableClickOutside = false,
        controlledIsOpen,
        onOpenChange,
        placement = "bottom",
        fullscreenOnMobile = false,
        enableAutoScroll = false,
        inlineOnMobile = false,
        dropdownZIndex,
        dropdownOffset,
        getDayTooltip,
        getDayClassName,
        hideOtherMonthDaysContent,
        rightOfCalendar,
    } = props;
    const {
        snapshot,
        isOpen,
        refs,
        floatingStyles,
        getReferenceProps,
        getFloatingProps,
        handleDayClick,
        handleDayHover,
        handleDayLeave,
        handleClear,
        formatDisplayValue,
    } = useDateRangePicker({
        model,
        controlledIsOpen,
        onOpenChange,
        disableClickOutside,
        isLoading,
        placement,
        fullscreenOnMobile,
        enableAutoScroll,
        dropdownOffset,
    });

    const isMobileViewport = useMatchMedia({maxWidth: 767});
    const isSingleCalendar = singleCalendar || isMobileViewport;
    const isInlineMode = inlineOnMobile && isMobileViewport;
    const {swipeHandlers, animationDirection} = useSwipeAnimation(model);

    const nightsCount = useMemo(() => {
        if (!snapshot.startDate || !snapshot.endDate) return null;
        return Math.round((snapshot.endDate.getTime() - snapshot.startDate.getTime()) / (1000 * 60 * 60 * 24));
    }, [snapshot.startDate, snapshot.endDate]);

    const triggerProps: DateRangePickerTriggerProps = {
        startDate: snapshot.startDate,
        endDate: snapshot.endDate,
        startPlaceholder,
        endPlaceholder,
        isOpen,
        isLoading,
        disabled: snapshot.disabled || isLoading,
        hasValue: snapshot.hasValue,
        onClick: () => {
            /* empty */
        },
        onClear: handleClear,
        formatDisplayValue,
    };

    const headerProps = useMemo(
        () => ({
            startDate: snapshot.startDate,
            endDate: snapshot.endDate,
            nightsCount,
            onClear: handleClear,
        }),
        [snapshot.startDate, snapshot.endDate, nightsCount, handleClear],
    );

    const footerContent =
        showClearButton && snapshot.hasValue ? (
            <div className="daterange-picker-field__footer">
                <ButtonBase variant="secondary-outline" onClick={handleClear} size="sm">
                    Clear
                </ButtonBase>
            </div>
        ) : null;

    const calendarContainerProps = {
        model,
        isSingle: isSingleCalendar,
        swipeHandlers,
        animationDirection,
        onDayClick: handleDayClick,
        onDayHover: handleDayHover,
        onDayLeave: handleDayLeave,
        getDayTooltip,
        getDayClassName,
        hideOtherMonthDaysContent,
    };

    // Inline mode: calendar always visible, no trigger
    if (isInlineMode) {
        return (
            <div className={`daterange-picker-field daterange-picker-field--inline ${className || ""}`}>
                {label && (
                    <Typography as="label" size={14} weight={500} color="title" className="daterange-picker-field__label">
                        {label}
                    </Typography>
                )}
                <div className={`daterange-picker-field__inline-content ${isLoading ? "daterange-picker-field__inline-content--loading" : ""}`}>
                    {isLoading && <LoadingOverlay />}
                    {renderHeader?.(headerProps)}
                    <div
                        className={`daterange-picker-field__calendar-wrap${rightOfCalendar ? " daterange-picker-field__calendar-wrap--with-right" : ""}`}
                    >
                        <CalendarContainer {...calendarContainerProps} isSingle={true} />
                        {rightOfCalendar && <div className="daterange-picker-field__calendar-right">{rightOfCalendar}</div>}
                    </div>
                    {children && <div className="daterange-picker-field__content">{children}</div>}
                    {footerContent}
                </div>
            </div>
        );
    }

    return (
        <div className={`daterange-picker-field ${className || ""}`}>
            {label && (
                <Typography as="label" size={14} weight={500} color="title" className="daterange-picker-field__label">
                    {label}
                </Typography>
            )}

            <div ref={refs.setReference} {...getReferenceProps()} className="daterange-picker-field__trigger-wrapper">
                {renderTrigger(triggerProps)}
            </div>

            {isOpen && !snapshot.disabled && (
                <div
                    ref={refs.setFloating}
                    style={{...floatingStyles, ...(dropdownZIndex && {zIndex: dropdownZIndex})}}
                    {...getFloatingProps()}
                    className={`daterange-picker-field__dropdown ${isSingleCalendar ? "daterange-picker-field__dropdown--single" : ""} ${fullscreenOnMobile ? "daterange-picker-field__dropdown--fullscreen-mobile" : ""} ${isLoading ? "daterange-picker-field__dropdown--loading" : ""}`}
                >
                    {isLoading && <LoadingOverlay />}
                    {renderHeader?.(headerProps)}
                    <CalendarContainer {...calendarContainerProps} />
                    {children && <div className="daterange-picker-field__content">{children}</div>}
                    {footerContent}
                </div>
            )}
        </div>
    );
}
